import React, {
  FC,
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  GuestValidationInput,
  GuestValidationResponseData,
  ResponseData,
  GetItemsResponseData,
  StatementsResponseData,
  PublicGuestValidationResponseData,
  PublicGuestValidationInput,
  HistoryRequestData,
  PaymentsHistoryResponseData,
  PaymentPlansResponseData,
  PaymentPlansRequestInput,
  PaymentPlanDetailInput,
  PaymentPlanDetailResponseData,
  CreatePaymentInput,
  CreatePaymentResponseData,
  ConfirmPaymentInput,
  PaymentPlanPreviewRequestInput,
  PaymentPlanPreviewResponseData,
  CreatePaymentPlanInput,
  ConfirmPaymentPlanInput,
  CreatePaymentPlanResponseData,
  CreatePayFullBalanceResponseData,
  UpdatePaymentMethodInput,
  UpdatePaymentResponseData,
  ConfirmPayFullBalanceInput,
  VerifyLoginResponseData,
  PDFResponse,
  PostBillErrors,
  PostBillError,
  AuthMethod,
  RefreshCTSTokenErrorData,
  GetStatementRequestInput,
  StatementStatus,
  PaymentDueStatus,
  PastDueType,
  ReviewAndPayStatus,
  SubmitSurveyInput,
} from "@app/postBill/models";
import { usePostBillApiController } from "@app/postBill/hooks/useApiController";
import { logMsg } from "@app/utils/logMsg";
import { PostBillContext } from "./postBillProvider";
import { routes } from "@app/routing";
import {
  getPostbillLocalStorage,
  updatePostbillLocalStorage,
} from "@app/postBill/utils/localStorage";
import { useBillingAccess } from "./billingAccessContext";
import { usePostbillSessionProvider } from "./postbillSessionProvider";
import { getTokenExpireTimeFromJwt } from "../utils";

type PostBillAPIProviderState = {
  loadingStates: Record<string, boolean>;
  firstName?: string;
  lastName?: string;
  ctsToken?: string;
  billingData?: GetItemsResponseData;
  verifyLoginData?: VerifyLoginResponseData;
  paymentSetupData?: CreatePaymentResponseData;
  paymentPlanSetupData?: CreatePaymentPlanResponseData;
  statementsData?: StatementsResponseData;
  paymentsHistoryData?: PaymentsHistoryResponseData;
  paymentPlansData?: PaymentPlansResponseData;
  updatePaymentSetupData?: UpdatePaymentResponseData;
  payFullBalanceData?: CreatePayFullBalanceResponseData;
};

export interface PostBillAPIController {
  apiProviderState: PostBillAPIProviderState;
  getItems: () => Promise<ResponseData<GetItemsResponseData>>;
  getPaymentPlans: (
    query: PaymentPlansRequestInput
  ) => Promise<ResponseData<PaymentPlansResponseData>>;
  guestValidation: (
    input: GuestValidationInput
  ) => Promise<ResponseData<GuestValidationResponseData>>;
  createPayment: (
    input: CreatePaymentInput
  ) => Promise<ResponseData<CreatePaymentResponseData>>;
  confirmPayment: (input: ConfirmPaymentInput) => Promise<ResponseData>;
  createPaymentPlan: (
    input: CreatePaymentPlanInput
  ) => Promise<ResponseData<CreatePaymentPlanResponseData>>;
  createPayFullBalance: (
    paymentPlanId: string
  ) => Promise<ResponseData<CreatePayFullBalanceResponseData>>;
  confirmPaymentPlan: (input: ConfirmPaymentPlanInput) => Promise<ResponseData>;
  confirmPayFullBalance: (
    input: ConfirmPayFullBalanceInput
  ) => Promise<ResponseData>;
  getStatements: (
    input: HistoryRequestData
  ) => Promise<ResponseData<StatementsResponseData>>;
  getPaymentsHistory: (
    input: HistoryRequestData
  ) => Promise<ResponseData<PaymentsHistoryResponseData>>;
  getPaymentPlanDetail: (
    input: PaymentPlanDetailInput
  ) => Promise<ResponseData<PaymentPlanDetailResponseData>>;
  publicLogin: (
    input: PublicGuestValidationInput
  ) => Promise<ResponseData<PublicGuestValidationResponseData>>;
  loadingStates: Record<string, boolean>;
  getPaymentPlanPreview: (
    input: PaymentPlanPreviewRequestInput
  ) => Promise<ResponseData<PaymentPlanPreviewResponseData>>;
  updatePayment: () => Promise<ResponseData<UpdatePaymentResponseData>>;
  updatePaymentMethod: (
    input: UpdatePaymentMethodInput
  ) => Promise<ResponseData>;
  verifyLogin: () => Promise<ResponseData<VerifyLoginResponseData>>;
  getReceipt: (paymentIntentId: string) => Promise<PDFResponse>;
  getStatementPdf: (input: GetStatementRequestInput) => Promise<PDFResponse>;
  getCTSToken: () => string | null;
  setCTSToken: (token: string) => void;
  submitSurvey: (input: SubmitSurveyInput) => Promise<ResponseData>;
  recordSurveyViewed: () => Promise<void>;
}

export const Context = createContext<PostBillAPIController>({
  apiProviderState: {
    loadingStates: {
      getItems: false,
      verifyLogin: false,
      publicLogin: false,
      submitSurvey: false,
    },
  },
  getItems: async () => Promise.reject(new Error()),
  getPaymentPlans: async () => Promise.reject(new Error()),
  guestValidation: async () => Promise.reject(new Error()),
  createPayment: async () => Promise.reject(new Error()),
  confirmPayment: async () => Promise.reject(new Error()),
  createPaymentPlan: async () => Promise.reject(new Error()),
  createPayFullBalance: async () => Promise.reject(new Error()),
  confirmPaymentPlan: async () => Promise.reject(new Error()),
  confirmPayFullBalance: async () => Promise.reject(new Error()),
  getStatements: async () => Promise.reject(new Error()),
  getPaymentsHistory: async () => Promise.reject(new Error()),
  publicLogin: async () => Promise.reject(new Error()),
  getPaymentPlanPreview: async () => Promise.reject(new Error()),
  getPaymentPlanDetail: async () => Promise.reject(new Error()),
  updatePayment: async () => Promise.reject(new Error()),
  updatePaymentMethod: async () => Promise.reject(new Error()),
  verifyLogin: async () => Promise.reject(new Error()),
  getReceipt: async () => Promise.reject(new Error()),
  getStatementPdf: async () => Promise.reject(new Error()),
  loadingStates: {},
  getCTSToken: () => null,
  setCTSToken: () => undefined,
  submitSurvey: async () => Promise.reject(new Error()),
  recordSurveyViewed: async () => undefined,
});

Context.displayName = "PostBillAPIContext";

export { Context as PostBillAPIContext };

interface PostBillAPIProviderProps {
  children: ReactNode;
}

const PostBillAPIProvider: FC<PostBillAPIProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { setBillingAccess } = useBillingAccess();
  const { setExpireTime } = usePostbillSessionProvider();
  const [apiProviderState, setApiProviderState] =
    useState<PostBillAPIProviderState>({
      loadingStates: {
        getItems: false,
        verifyLogin: false,
        publicLogin: false,
      },
    });
  const {
    setPatientUid,
    paidDirectPayments,
    paidPaymentPlans,
    setPaymentCompleted,
  } = useContext(PostBillContext);
  const ctsTokenRef = useRef<string | null>(null);

  const setLoadingState = (key: string, value: boolean) => {
    setApiProviderState((prevState) => ({
      ...prevState,
      loadingStates: {
        ...prevState.loadingStates,
        [key]: value,
      },
    }));
  };

  //filter already paid items from BE response if it appears in it
  const filterPaidBillingItems = (
    data: GetItemsResponseData
  ): GetItemsResponseData | undefined => {
    if (!data) return;

    const anyChange = paidDirectPayments.size > 0 || paidPaymentPlans.size > 0;

    let updatedReadyToPay = data.readyToPay.map((item) => {
      if (
        paidDirectPayments.size > 0 &&
        paidDirectPayments.has(item.lineItemUid) &&
        item.statementStatus !== "PAYMENT_PENDING"
      ) {
        return {
          ...item,
          statementStatus: StatementStatus.PAYMENT_PENDING,
          dueDate: { pastDueExists: PaymentDueStatus.EMPTY },
        };
      }
      return item;
    });

    if (paidPaymentPlans.size > 0) {
      updatedReadyToPay = updatedReadyToPay.filter(
        (item) => !paidPaymentPlans.has(item.lineItemUid)
      );
    }

    let pastDueExists = data.pastDueExists;
    let displayDate = data.displayDate;
    let pastDueType = data.pastDueType;
    let balanceDateStatus = data.balanceDateStatus;

    if (anyChange) {
      if (updatedReadyToPay.length === 0) {
        pastDueExists = false;
        displayDate = { pastDueExists: PaymentDueStatus.EMPTY };
        pastDueType = PastDueType.NO_PAST_DUE;
        balanceDateStatus = ReviewAndPayStatus.NOTHING_TO_PAY;
      } else if (
        updatedReadyToPay.every((i) => i.statementStatus === "PAYMENT_PENDING")
      ) {
        pastDueExists = false;
        displayDate = {
          pastDueExists: PaymentDueStatus.VALID_DATE,
          dueDate: new Date().toISOString(),
        };
        pastDueType = PastDueType.NO_PAST_DUE;
        balanceDateStatus = ReviewAndPayStatus.PAYMENT_PENDINGS_ONLY;
      }
    }

    const updatedBillingData: GetItemsResponseData = {
      ...data,
      readyToPay: updatedReadyToPay,
      pastDueExists,
      displayDate,
      pastDueType,
      balanceDateStatus,
    };

    return updatedBillingData;
  };

  const getCTSToken = (): string | null => {
    return apiProviderState.ctsToken || null;
  };

  const setCTSToken = (token: string) => {
    ctsTokenRef.current = token;
    setApiProviderState((prevState) => ({
      ...prevState,
      ctsToken: token,
    }));
  };

  const extractAndStoreCTSToken = (
    responseData:
      | GuestValidationResponseData
      | PublicGuestValidationResponseData
  ): string | null => {
    const token = responseData?.token;
    if (token) {
      setCTSToken(token);

      const tokenExpireTimestamp = getTokenExpireTimeFromJwt(token)?.exp;
      const expiresAt = tokenExpireTimestamp * 1000;
      setExpireTime(expiresAt);
      return token;
    }
    return null;
  };

  const {
    getItems,
    getPaymentPlans,
    guestValidation,
    createPayment,
    confirmPayment,
    createPaymentPlan,
    createPayFullBalance,
    confirmPaymentPlan,
    confirmPayFullBalance,
    getStatements,
    getPaymentsHistory,
    publicLogin,
    getPaymentPlanPreview,
    getPaymentPlanDetail,
    updatePayment,
    updatePaymentMethod,
    verifyLogin,
    getReceipt,
    getStatementPdf,
    submitSurvey,
  } = usePostBillApiController(ctsTokenRef);

  //handling errors which return from fetch with cts token requests
  const handleError = async <T,>(
    errorData: PostBillError | ResponseData<RefreshCTSTokenErrorData>,
    retryRequest: (isRetried?: boolean) => Promise<ResponseData<T>>,
    isRetried = false
  ) => {
    if (errorData.error === PostBillErrors.CTS_TOKEN_EXPIRED) {
      const authMethod = errorData?.data?.authMethod;

      switch (authMethod) {
        case AuthMethod.LOGIN:
          if (isRetried) {
            throw new Error(errorData.error as PostBillErrors);
          }

          try {
            await handleVerifyLogin();
            return retryRequest(true);
          } catch (error) {
            throw new Error(error.error as PostBillErrors);
          }
        case AuthMethod.PUBLIC:
          navigate(routes.billingCenterGuestLogin, { replace: true });
          break;
        case AuthMethod.TOKENIZED_LINK: {
          const token =
            errorData?.data?.tokenizedLink ??
            getPostbillLocalStorage("guestToken");
          navigate(`${routes.postbillGuestLogin}?token=${token}`, {
            replace: true,
          });
          break;
        }
        default:
          navigate(routes.home, { replace: true });
          break;
      }
    }

    throw new Error(errorData.error as PostBillErrors);
  };

  const handleGetItems = async (
    isRetried?: boolean
  ): Promise<ResponseData<GetItemsResponseData>> => {
    try {
      setLoadingState("getItems", true);
      const response = await getItems();
      const filteredData = filterPaidBillingItems(
        response?.data as GetItemsResponseData
      );
      setApiProviderState((prevState) => ({
        ...prevState,
        billingData: filteredData,
      }));
      setPaymentCompleted(false);
      return response;
    } catch (error) {
      return handleError(error, handleGetItems, isRetried);
    } finally {
      setLoadingState("getItems", false);
    }
  };

  const handleGetPaymentPlans = async (
    query: PaymentPlansRequestInput,
    isRetried?: boolean
  ): Promise<ResponseData<PaymentPlansResponseData>> => {
    try {
      const response = await getPaymentPlans(query);
      setApiProviderState((prevState) => ({
        ...prevState,
        paymentPlansData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleGetPaymentPlans(query, isRetried),
        isRetried
      );
    }
  };

  const handleGuestValidation = async (
    data: GuestValidationInput
  ): Promise<ResponseData<GuestValidationResponseData>> => {
    try {
      const response = await guestValidation(data);

      if (response.data) {
        extractAndStoreCTSToken(response?.data);
      }
      return response;
    } catch (error) {
      logMsg("error", "postbill.api", "guest validation failed");
      throw error;
    }
  };

  const handleCreatePayment = async (
    data: CreatePaymentInput,
    isRetried?: boolean
  ): Promise<ResponseData<CreatePaymentResponseData>> => {
    try {
      const response = await createPayment(data);
      setApiProviderState((prevState) => ({
        ...prevState,
        paymentSetupData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleCreatePayment(data, isRetried),
        isRetried
      );
    }
  };

  const handleUpdatePayment = async (
    isRetried?: boolean
  ): Promise<ResponseData<UpdatePaymentResponseData>> => {
    try {
      const response = await updatePayment();

      return response;
    } catch (error) {
      return handleError(error, handleUpdatePayment, isRetried);
    }
  };

  const handleUpdatePaymentMethod = async (
    data: UpdatePaymentMethodInput,
    isRetried?: boolean
  ): Promise<ResponseData> => {
    try {
      const response = await updatePaymentMethod(data);
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleUpdatePaymentMethod(data, isRetried),
        isRetried
      );
    }
  };

  const handleConfirmPayment = async (
    data: ConfirmPaymentInput,
    isRetried?: boolean
  ): Promise<ResponseData> => {
    try {
      const response = await confirmPayment(data);
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleConfirmPayment(data, isRetried),
        isRetried
      );
    }
  };

  const handleCreatePaymentPlan = async (
    data: CreatePaymentPlanInput,
    isRetried?: boolean
  ): Promise<ResponseData<CreatePaymentPlanResponseData>> => {
    try {
      const response = await createPaymentPlan(data);
      setApiProviderState((prevState) => ({
        ...prevState,
        paymentPlanSetupData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleCreatePaymentPlan(data, isRetried),
        isRetried
      );
    }
  };

  const handleCreatePayFullBalance = async (
    paymentPlanId: string,
    isRetried?: boolean
  ): Promise<ResponseData<CreatePayFullBalanceResponseData>> => {
    try {
      const response = await createPayFullBalance(paymentPlanId);
      setApiProviderState((prevState) => ({
        ...prevState,
        payFullBalanceData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleCreatePayFullBalance(paymentPlanId, isRetried),
        isRetried
      );
    }
  };

  const handleConfirmPaymentPlan = async (
    data: ConfirmPaymentPlanInput,
    isRetried?: boolean
  ): Promise<ResponseData> => {
    try {
      const response = await confirmPaymentPlan(data);
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleConfirmPaymentPlan(data, isRetried),
        isRetried
      );
    }
  };

  const handleConfirmPayFullBalance = async (
    input: ConfirmPayFullBalanceInput,
    isRetried?: boolean
  ): Promise<ResponseData> => {
    try {
      const response = await confirmPayFullBalance(input);
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleConfirmPayFullBalance(input, isRetried),
        isRetried
      );
    }
  };

  const handleGetStatements = async (
    input: HistoryRequestData,
    isRetried?: boolean
  ): Promise<ResponseData<StatementsResponseData>> => {
    try {
      const response = await getStatements(input);
      setApiProviderState((prevState) => ({
        ...prevState,
        statementsData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleGetStatements(input, isRetried),
        isRetried
      );
    }
  };

  const handleGetPaymentsHistory = async (
    input: HistoryRequestData,
    isRetried?: boolean
  ): Promise<ResponseData<PaymentsHistoryResponseData>> => {
    try {
      const response = await getPaymentsHistory(input);
      setApiProviderState((prevState) => ({
        ...prevState,
        paymentsHistoryData: response?.data,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleGetPaymentsHistory(input, isRetried),
        isRetried
      );
    }
  };

  const handlePublicLogin = async (
    data: PublicGuestValidationInput
  ): Promise<ResponseData<PublicGuestValidationResponseData>> => {
    try {
      setLoadingState("publicLogin", true);
      const response = await publicLogin(data);

      if (response.data) {
        extractAndStoreCTSToken(response.data);
      }

      setApiProviderState((prevState) => ({
        ...prevState,
        firstName: response?.data?.patient.firstName,
        lastName: response?.data?.patient.lastName,
      }));
      setPatientUid(response?.data?.patient.uid as string);
      updatePostbillLocalStorage("authType", "public");
      return response;
    } catch (error) {
      logMsg("error", "postbill.api", "public login failed");
      throw error;
    } finally {
      setLoadingState("publicLogin", false);
    }
  };

  const handleGetPaymentPlanPreview = async (
    data: PaymentPlanPreviewRequestInput,
    isRetried?: boolean
  ): Promise<ResponseData<PaymentPlanPreviewResponseData>> => {
    try {
      setLoadingState("getPaymentPlanPreview", true);
      const response = await getPaymentPlanPreview(data);
      setApiProviderState((prevState) => ({
        ...prevState,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleGetPaymentPlanPreview(data, isRetried),
        isRetried
      );
    } finally {
      setLoadingState("getPaymentPlanPreview", false);
    }
  };

  const handlePaymentPlanDetail = async (
    data: PaymentPlanDetailInput,
    isRetried?: boolean
  ): Promise<ResponseData<PaymentPlanDetailResponseData>> => {
    try {
      setLoadingState("getPaymentDetails", true);
      const response = await getPaymentPlanDetail(data);
      setApiProviderState((prevState) => ({
        ...prevState,
      }));
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handlePaymentPlanDetail(data, isRetried),
        isRetried
      );
    } finally {
      setLoadingState("getPaymentDetails", false);
    }
  };

  const handleVerifyLogin = async (): Promise<
    ResponseData<VerifyLoginResponseData>
  > => {
    try {
      setLoadingState("verifyLogin", true);
      setBillingAccess(undefined, true, false);

      const response = await verifyLogin();
      if (response.data) {
        extractAndStoreCTSToken(response.data);

        const hasAccess = response.data.displayBillingCenter ?? false;
        setBillingAccess(hasAccess, false, false);
      }

      return response;
    } catch (error) {
      logMsg("error", "postbill.api", "login verification failed");
      setBillingAccess(false, false, true);
      throw error;
    } finally {
      setLoadingState("verifyLogin", false);
    }
  };

  const handleGetReceipt = async (
    paymentIntentId: string
  ): Promise<PDFResponse> => {
    try {
      setLoadingState("getReceipt", true);
      return await getReceipt(paymentIntentId);
    } catch (error) {
      logMsg("error", "postbill.api", "receipt fetch failed");
      throw error;
    } finally {
      setLoadingState("getReceipt", false);
    }
  };

  const handleGetStatementPdf = async (
    data: GetStatementRequestInput
  ): Promise<PDFResponse> => {
    try {
      setLoadingState("getStatementPdf", true);
      return await getStatementPdf(data);
    } catch (error) {
      logMsg("error", "postbill.api", "statement PDF fetch failed");
      throw error;
    } finally {
      setLoadingState("getStatementPdf", false);
    }
  };

  const handleSubmitSurvey = async (
    data: SubmitSurveyInput,
    isRetried?: boolean
  ): Promise<ResponseData> => {
    try {
      setLoadingState("submitSurvey", true);
      const response = await submitSurvey(data);
      return response;
    } catch (error) {
      return handleError(
        error,
        (isRetried) => handleSubmitSurvey(data, isRetried),
        isRetried
      );
    } finally {
      setLoadingState("submitSurvey", false);
    }
  };

  const handleRecordSurveyViewed = async (
    isRetried?: boolean
  ): Promise<void> => {
    try {
      await submitSurvey({ eventType: "view" });
    } catch (error) {
      try {
        await handleError(
          error,
          (retry) =>
            handleRecordSurveyViewed(retry) as unknown as Promise<
              ResponseData<unknown>
            >,
          isRetried
        );
      } catch {
        logMsg("error", "postbill.api", "Failed to record survey viewed");
      }
    }
  };

  const value = {
    apiProviderState,
    getItems: handleGetItems,
    getPaymentPlans: handleGetPaymentPlans,
    guestValidation: handleGuestValidation,
    createPayment: handleCreatePayment,
    confirmPayment: handleConfirmPayment,
    createPaymentPlan: handleCreatePaymentPlan,
    createPayFullBalance: handleCreatePayFullBalance,
    confirmPaymentPlan: handleConfirmPaymentPlan,
    confirmPayFullBalance: handleConfirmPayFullBalance,
    getStatements: handleGetStatements,
    getPaymentsHistory: handleGetPaymentsHistory,
    publicLogin: handlePublicLogin,
    getPaymentPlanPreview: handleGetPaymentPlanPreview,
    getPaymentPlanDetail: handlePaymentPlanDetail,
    updatePayment: handleUpdatePayment,
    updatePaymentMethod: handleUpdatePaymentMethod,
    verifyLogin: handleVerifyLogin,
    getReceipt: handleGetReceipt,
    getStatementPdf: handleGetStatementPdf,
    loadingStates: apiProviderState.loadingStates ?? {},
    getCTSToken,
    setCTSToken,
    submitSurvey: handleSubmitSurvey,
    recordSurveyViewed: handleRecordSurveyViewed,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default PostBillAPIProvider;

// Export hook for other components to access billing state
export const useBillingAccessState = () => {
  const { loadingStates, apiProviderState } = useContext(Context);

  return {
    hasAccess: apiProviderState.verifyLoginData?.displayBillingCenter ?? false,
    isLoading: loadingStates.verifyLogin,
  };
};
